import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useOrtofoto } from '../../hooks/useOrtofoto';
import { agregarCapaOrtofoto } from '../../utils/capaOrtofoto';


interface Props {
  center: [number, number];
  zoom: number;
  markers: Array<{
    id: string;
    lat: number;
    lng: number;
    status: 'good' | 'warn' | 'alert' | 'offline';
    label: string;
    /** Nombre completo, para el tooltip del navegador al pasar el mouse. */
    title?: string;
  }>;
  onMarkerClick?: (id: string) => void;
}

export default function SensorMap({ center, zoom, markers, onMarkerClick }: Props) {
  const { t } = useTranslation();
  const { vuelo } = useOrtofoto();
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onMarkerClickRef = useRef(onMarkerClick);
  useEffect(() => { onMarkerClickRef.current = onMarkerClick; });

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let map: L.Map;
    try {
      // maxZoom 21 y no el 18 de fábrica: la ortofoto del dron tiene
      // detalle hasta ese nivel y sin esto no se podría llegar a verlo.
      map = L.map(containerRef.current, { maxZoom: 21 }).setView(center, zoom);
    } catch (err) {
      console.warn('[SensorMap] Leaflet init failed:', err);
      return;
    }

    // Tiles ESRI World Imagery
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      // ESRI tiene detalle real hasta el 19; más allá el navegador estira lo
      // que hay. Se deja estirar en vez de dejar hoyos blancos.
      { attribution: 'ESRI', maxZoom: 21, maxNativeZoom: 19 }
    ).addTo(map);

    // Labels overlay
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      { opacity: 0.6 }
    ).addTo(map);

    mapRef.current = map;

    /**
     * Avisarle a Leaflet cuando su contenedor cambia de tamaño.
     *
     * Leaflet mide el contenedor UNA vez, al crearse, y nunca vuelve a
     * mirar. Si el alto cambia después —y aquí cambia solo: la columna de
     * al lado crece cuando terminan de cargar los sensores— sigue pintando
     * mosaicos del tamaño viejo y el resto del recuadro queda gris, como
     * si el mapa se hubiera quedado a medias.
     *
     * Es un bug que no da ningún error y sólo se ve. Va aquí, en el
     * componente compartido, para que ninguna pantalla que use este mapa
     * tenga que acordarse de él.
     */
    const observador = new ResizeObserver(() => map.invalidateSize());
    observador.observe(containerRef.current);

    return () => {
      observador.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  /**
   * Fondo con la imagen georreferenciada del cliente, si tiene alguna.
   *
   * Va encima de la satelital y debajo de los marcadores, así que los
   * sensores quedan sobre la foto real de su predio —reciente y con
   * centímetros por pixel— en vez de sobre una satelital que puede tener
   * años. El control de la esquina deja volver a la satelital cuando
   * convenga: para ver los alrededores de la zona cubierta, por ejemplo.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !vuelo) return;
    return agregarCapaOrtofoto(map, vuelo, t('maps.imageLayer'));
  }, [vuelo, t]);


  /**
   * Encuadra el mapa para que se vean TODOS los sensores, y sólo cuando
   * cambian de lugar.
   *
   * Antes esto era `setView(center, zoom)` con dependencias [center, zoom],
   * y tenía dos problemas encima:
   *
   *  1. `center` es un arreglo que el padre construye nuevo en cada
   *     renderizado, así que React lo veía distinto SIEMPRE y la vista se
   *     replantaba sola. Cada lectura de telemetría que llegaba arrancaba al
   *     usuario de donde estuviera mirando — el mapa peleaba contra él.
   *  2. Centraba en el PROMEDIO de las coordenadas a un zoom fijo. Con
   *     sensores separados, ese promedio cae en medio de la nada y se abría
   *     acercado sobre un punto donde no hay nada que ver.
   *
   * Ahora se calcula un encuadre que contiene a todos y sólo se aplica si
   * las POSICIONES cambiaron — un cambio de estado o de batería ya no mueve
   * la vista. Arrastrar y hacer zoom se quedan donde el usuario los dejó.
   */
  const firmaPosiciones = useRef<string>('');
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const puntos = markers
      .filter(m => Number.isFinite(m.lat) && Number.isFinite(m.lng))
      .map(m => [Number(m.lat), Number(m.lng)] as [number, number]);

    const firma = puntos.map(p => p.join(',')).sort().join('|');
    if (firma === firmaPosiciones.current) return;
    firmaPosiciones.current = firma;

    if (puntos.length === 0) {
      map.setView(center, zoom);
    } else if (puntos.length === 1) {
      // Encuadrar un solo punto daría el zoom máximo posible, pegado al
      // suelo. Se usa un acercamiento cómodo para ver el entorno.
      map.setView(puntos[0]!, Math.max(zoom, 16));
    } else {
      // `maxZoom` evita el otro extremo: dos sensores a diez metros no deben
      // abrir el mapa tan cerca que no se distinga dónde están.
      map.fitBounds(L.latLngBounds(puntos), { padding: [42, 42], maxZoom: 17 });
    }
  }, [markers, center, zoom]);

  // Actualizar marcadores cuando cambian
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) map.removeLayer(layer);
    });

    markers.forEach((m) => {
      // Validación de coordenadas para evitar crash de Leaflet
      if (m.lat === undefined || m.lng === undefined || isNaN(m.lat) || isNaN(m.lng)) {
        console.warn(`[SensorMap] Invalid coordinates for marker ${m.label}`, m);
        return;
      }

      // 'offline' en gris a propósito: es un device dado de alta que todavía
      // no manda datos (o dejó de mandarlos). Sin este caso caía en el `else`
      // y se pintaba de rojo, indistinguible de una alarma real.
      const color =
        m.status === 'good'
          ? '#1D9E75'
          : m.status === 'warn'
          ? '#EF9F27'
          : m.status === 'offline'
          ? '#9AA0A6'
          : '#E24B4A';

      const cursor = onMarkerClick ? 'pointer' : 'default';
      const icon = L.divIcon({
        html: `<div style="width:32px;height:32px;border-radius:50%;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid white;cursor:${cursor};transition:transform 0.15s;"
          onmouseenter="this.style.transform='scale(1.2)'"
          onmouseleave="this.style.transform='scale(1)'"
        >${m.label}</div>`,
        className: '',
      });

      // `title` va por opción de Leaflet y NO interpolado en el html del
      // icono: es texto que viene de la base (la etiqueta del device) y
      // meterlo al innerHTML permitiría inyectar marcado desde el nombre.
      const marker = L.marker([m.lat, m.lng], { icon, title: m.title }).addTo(map);
      if (onMarkerClickRef.current) marker.on('click', () => onMarkerClickRef.current?.(m.id));
    });
  }, [markers]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%', borderRadius: '12px', overflow: 'hidden' }} />;
}
